<template>
  <div class="subdetail-page">
    <v-app-bar color="#4caf50" dark dense>
      <v-btn icon @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="font-weight-bold">
        <v-icon class="mr-2">mdi-check-all</v-icon>
        Publish Detail KPI
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      :elevation="6"
      top
      shaped
      multi-line
      class="custom-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon 
          class="mr-3" 
          :color="snackbar.color === 'success' ? 'white' : ''"
          size="24"
        >
          {{ snackbar.icon }}
        </v-icon>
        <div>
          <div class="font-weight-bold text-subtitle-1 mb-1">
            {{ snackbar.title }}
          </div>
          <div class="text-body-2">{{ snackbar.text }}</div>
        </div>
        <v-btn
          icon
          small
          class="ml-4"
          @click="snackbar.show = false"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>
    <v-container fluid class="pa-6">
      <div v-if="isLoadingData" class="text-center pa-8">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="64"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Loading data...</div>
      </div>
      <div v-else>
        <v-card class="mb-6 elevation-2" style="border: 4px solid rgba(0, 56, 113, 0.2)">
          <v-card-title class="subtitle-1 font-weight-bold">KPI Target</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="ownerSetsHeaders"
              :items="ownerSetsItems"
              :items-per-page="5"
              :page.sync="ownerSetsPage"
              dense
              class="owner-sets-table elevation-0"
              :footer-props="{
                itemsPerPageOptions: [5,10, 25, 50],
                showFirstLastPage: true
              }"
            >
              <template v-slot:no-data>
                <div class="pa-4 text-center grey--text">Tidak ada data KPI Target</div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
        <v-card class="elevation-2" style="border: 4px solid rgba(76, 175, 80, 0.2)">
          <v-card-title class="subtitle-1 font-weight-bold">Data Bulanan</v-card-title>
          <v-card-text>
            <v-simple-table dense class="monthly-data-table">
              <thead>
                <tr>
                  <th class="text-center text-white">Owner</th>
                  <th class="text-center text-white">Status KPI</th>
                  <th class="text-center text-white">Sharing</th>
                  <th class="text-center text-white">KPI Name</th>
                  <th class="text-center text-white">Annual Target</th>
                  <th class="text-center text-white">Kalkulasi</th>
                  <th v-for="month in displayMonths"
                      :key="`${month.name}-${month.year}`" class="text-center text-white">
                    {{ month.label }}
                  </th>
                  <th class="text-center text-white">Total</th>
				  <th class="text-center text-white">Manual Adjustment</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(kpi, kpiIndex) in kpiData">
                  <template v-for="(ownerSet, ownerIndex) in kpi.ownerSets">
                    <tr :key="`monthly-${kpiIndex}-${ownerIndex}`" v-if="ownerSet.annualTarget !== null && ownerSet.annualTarget !== undefined"
                        :class="getRowClass(kpi, kpiIndex, ownerIndex)">
                      <td class="text-body-1">{{ getOwnerDisplayForShared(kpi, ownerSet.owner) }}</td>
                      <td class="text-body-1" :class="getStatusClass(kpi, ownerIndex)">
                        <span v-if="ownerIndex === 0">{{ getDisplayStatus(kpi, ownerIndex) }}</span>
                      </td>
                      <td>{{ getSharingName(ownerSet.sharing) }}</td>
                      <td v-if="ownerIndex === 0" :rowspan="kpi.ownerSets.length" class="kpi-name-cell text-body-1">{{ kpi.kpiName }}</td>
                      <td class="text-body-1">{{formatThousandId(ownerSet.annualTarget) }}</td>
                      <td class="text-body-1">{{ kpi.kalkulasi }}</td>
                      <td v-for="month in displayMonths"
                          :key="`data-${kpiIndex}-${ownerIndex}-${month.name}-${month.year}`">
                        <v-text-field 
                        :value="formatThousandId(getMonthlyData(kpi, ownerIndex, month.name, month.year))" 
                        @input="handleMonthInput(kpi, ownerIndex, month.name, month.year, $event)" 
                        :disabled="isFieldDisabled(kpi, ownerIndex, month.name)"
                        dense outlined hide-details class="month-input text-right" type="text" inputmode="numeric"
                        style="text-align: right;"
                      />
                      </td>
                      <td class="font-weight-bold text-body-1">{{ kpi.ownerSets[ownerIndex].manual_adjustment ? formatThousandId(kpi.ownerSets[ownerIndex].annualTarget) : formatThousandId(calculateTotalForOwner(kpi, ownerIndex)) }}</td>
					  <td class="text-body-1">
                        <span>{{ kpi.ownerSets[ownerIndex].manual_adjustment ? "Yes" : "No" }}</span>
                        <v-tooltip v-if="kpi.ownerSets[ownerIndex].manual_adjustment" bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon 
                              small 
                              color="primary" 
                              class="ml-1" 
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-help-circle
                            </v-icon>
                          </template>
                          <span>Nilai total tidak dihitung dari akumulasi data, <br /> melainkan ditentukan berdasarkan Annual Target.</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </div>
      <v-card class="mt-6 elevation-2" style="background-color: #dff1e0;">
        <v-card-actions class="d-flex justify-end pa-6">
          <v-btn medium color="primary" class="mr-4" @click="saveData">
            <v-icon left>mdi-content-save</v-icon>
            Simpan
          </v-btn>
          <v-btn medium color="success" @click="publishData">
            <v-icon left>mdi-publish</v-icon>
            Publish
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
export default {
	name: "SubDetailPage",
	props: {
		kpiPeriodeId: {
			type: [String, Number],
			required: true,
		},
	},
	data() {
		return {
			autoSaveTimeout: null,
			ownerSetsPage: 1,
			isCalculating: false,
			hasUnsavedChanges: false,
			lastSaveTime: null,
			isLoadingData: true,
			isLoading: false,
			sessionId: null,
			periodeHeaders: [],
			validationErrors: [],
			kpiData: [],
			owners: [],
			sharingOptions: [],
			cekdialog: {},
			snackbar: {
				show: false,
				color: "warning",
				title: "",
				text: "",
				icon: "mdi-alert",
				timeout: 6000,
			},
		};
	},
	computed: {
		displayMonths() {
			if (this.periodeHeaders && this.periodeHeaders.length > 0) {
				return this.periodeHeaders.map((header) => ({
					name: this.getMonthNameFromKey(header.key),
					label: header.label,
					key: header.key,
					year: parseInt(header.key.split("-")[0]),
				}));
			}
			return [];
		},
		ownerSetsHeaders() {
			return [
				{
					text: "KPI Name",
					value: "kpiName",
					align: "center",
					class: "white--text",
				},
				{
					text: "Owner",
					value: "ownerDisplay",
					align: "center",
					class: "white--text",
				},
				{
					text: "Sharing",
					value: "sharingName",
					align: "center",
					class: "white--text",
				},
				{
					text: "Annual Target",
					value: "annualTarget",
					align: "center",
					class: "white--text",
				},
				{
					text: "Vertikal",
					value: "vertikal",
					align: "center",
					class: "white--text",
				},
				{
					text: "Kalkulasi",
					value: "kalkulasi",
					align: "center",
					class: "white--text",
				},
				{
					text: "Pengukuran",
					value: "pengukuran",
					align: "center",
					class: "white--text",
				},
				{
					text: "Sumber Data",
					value: "sumberData",
					align: "center",
					class: "white--text",
				},
				{
					text: "Higher/Lower Better",
					value: "higherLower",
					align: "center",
					class: "white--text",
				},
			];
		},
		ownerSetsItems() {
			if (!this.kpiData || this.kpiData.length === 0) return [];
			return this.kpiData.flatMap((kpi) =>
				(kpi.ownerSets || []).map((ownerSet) => ({
					kpiName: kpi.kpiName,
					ownerDisplay: this.getOwnerDisplayForShared(kpi, ownerSet.owner),
					sharingName: this.getSharingName(ownerSet.sharing),
					annualTarget: ownerSet.annualTarget,
					vertikal: kpi.vertikal,
					kalkulasi: kpi.kalkulasi,
					pengukuran: ownerSet.pengukuran,
					sumberData: ownerSet.sumberData,
					higherLower: ownerSet.higherLower,
				}))
			);
		},
	},
	async created() {
		this.cekdialog = {
			id: this.$route.params.chartId || this.kpiPeriodeId,
			chart_id: this.$route.params.chartId || this.kpiPeriodeId,
			kpi_periode_id: this.kpiPeriodeId,
		};
		try {
			await this.loadInitialData();
			await this.fetchKpiPeriodeHeaders();
			this.initializeMonthlyData();
			await this.GetKpiData();
			await this.$nextTick();
		} catch (error) {
			console.error("❌ Error in created lifecycle:", error);
		} finally {
			this.isLoadingData = false;
		}
	},
	methods: {
		goBack() {
			this.$router.push({
				path: "/home",
				query: {
					openDetail: true,
					chartId: this.$route.params.chartId,
					kpiPeriodeId: this.$route.params.kpiPeriodeId,
				},
			});
		},
		handleMonthInput(kpi, ownerIndex, month, year, input) {
			const numeric = this.normalizeNumberInput(input);
			this.setMonthlyData(kpi, ownerIndex, month, year, numeric);
		},
		normalizeNumberInput(value) {
			// null / undefined
			if (value === null || value === undefined) return null;

			// already number
			if (typeof value === "number") return value;

			// handle string type
			if (typeof value === "string") {
				let s = String(value).trim();

				// empty string
				if (s === "") return null;

				// allow single "-" as in-progress negative input
				if (s === "-") return null;

				// remove thousand separators "," before parsing
				s = s.replace(/,/g, "");

				// only allow: optional "-", digits, optional single decimal "."
				if (!/^-?\d*\.?\d*$/.test(s)) return null;

				const num = parseFloat(s);
				return Number.isNaN(num) ? null : num;
			}

			return null;
		},

		formatThousandId(value) {
			const num =
				typeof value === "number" ? value : this.normalizeNumberInput(value);

			if (num === null || Number.isNaN(num)) return "";

			// format with comma as thousand separator
			return new Intl.NumberFormat("en-US", {
				maximumFractionDigits: 2,
			}).format(num);
		},

		showSnackbar(options) {
			this.snackbar = {
				...this.snackbar,
				...options,
				show: true,
			};
		},

		async loadInitialData() {
			const chartId =
				this.$route.params.chartId || this.$route.params.kpiPeriodeId;

			try {
				const ownersResponse = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/kpi-item/owner/${chartId}?type=owners`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
							"Content-Type": "application/json",
						},
					}
				);
				const sharingResponse = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/kpi-item/sharing/${chartId}?type=sharing`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
							"Content-Type": "application/json",
						},
					}
				);
				this.owners = ownersResponse.data.map((owner) => ({
					text: `${owner.name} - ${owner.position_name}`,
					value: owner.id,
				}));
				this.sharingOptions = sharingResponse.data.map((sharing) => ({
					text: `${sharing.position_name} - ${sharing.name}`,
					value: sharing.id,
				}));
			} catch (error) {
				this.owners = [];
				this.sharingOptions = [];
			}
		},

		async GetKpiData() {
			const chartId =
				this.$route.params.chartId || this.$route.params.kpiPeriodeId;

			try {
				const response = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/kpi-item/${chartId}`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
						},
					}
				);
				this.kpiData = response.data.map((item) => {
					const convertedShares = this.convertShares(item.shares || []);
					let ownerSets = [
						{
							kpi_item_id: item.id,
							owner: item.owner_id,
							sharing: convertedShares,
							annualTarget: item.annual_target || 0,
							pengukuran: item.pengukuran || "-",
							sumberData: item.sumber_data || "-",
							higherLower: item.direction || "-",
							monthlyData: item.monthlyData || {},
							manual_adjustment: Boolean(item.manual_adjustment),
						},
					];
					if (item.children && item.children.length > 0) {
						item.children.forEach((child) => {
							const convertedChildShares = this.convertShares(
								child.shares || []
							);
							ownerSets.push({
								kpi_item_id: child.id,
								owner: child.owner_id,
								sharing: convertedChildShares,
								annualTarget: child.annual_target || 0,
								pengukuran: child.pengukuran || "-",
								sumberData: child.sumber_data || "-",
								higherLower: child.direction || "-",
								monthlyData: child.monthlyData || {},
								manual_adjustment: Boolean(child.manual_adjustment),
							});
						});
					}

					return {
						kpiName: item.kpi_name,
						vertikal: this.getVerticalText(item.vertical_id),
						kalkulasi: this.getKalkulasiName(item.kalkulasi_id),
						kalkulasiId: item.kalkulasi_id,
						verticalId: item.vertical_id,
						ownerSets: ownerSets,
						statusKpi: item.status_kpi || "Draft",
						values: item.values || [],
						children: item.children || [],
						isParent: item.children && item.children.length > 0,
						parentId: item.parent_id,
						itemId: item.id,
						manualAdjustment: Boolean(item.manual_adjustment),
					};
				});
				this.kpiData.forEach((kpi) => {
					if (kpi.values && Array.isArray(kpi.values)) {
						kpi.values.forEach((valueItem) => {
							if (valueItem.periode && valueItem.target) {
								const [year, month] = valueItem.periode.split("-");
								const monthNames = {
									"01": "january",
									"02": "february",
									"03": "march",
									"04": "april",
									"05": "may",
									"06": "june",
									"07": "july",
									"08": "august",
									"09": "september",
									10: "october",
									11: "november",
									12: "december",
								};
								const monthName = monthNames[month];

								if (monthName) {
									this.setMonthlyData(
										kpi,
										0,
										monthName,
										year,
										valueItem.target
									);
								}
							}
						});
					}
					if (kpi.children && Array.isArray(kpi.children)) {
						kpi.children.forEach((child, childIndex) => {
							if (child.values && Array.isArray(child.values)) {
								child.values.forEach((valueItem) => {
									if (valueItem.periode && valueItem.target) {
										const [year, month] = valueItem.periode.split("-");
										const monthNames = {
											"01": "january",
											"02": "february",
											"03": "march",
											"04": "april",
											"05": "may",
											"06": "june",
											"07": "july",
											"08": "august",
											"09": "september",
											10: "october",
											11: "november",
											12: "december",
										};
										const monthName = monthNames[month];

										if (monthName) {
											this.setMonthlyData(
												kpi,
												childIndex + 1,
												monthName,
												year,
												valueItem.target
											);
										}
									}
								});
							}
						});
					}
				});
			} catch (error) {
				this.kpiData = [];
			}
		},

		convertShares(shares) {
			if (!shares || shares.length === 0) {
				return [];
			}

			if (!this.sharingOptions || this.sharingOptions.length === 0) {
				return shares.map((share) => ({
					text: `Chart ID: ${share.chart_id}`,
					value: share.chart_id,
				}));
			}

			return shares.map((share) => {
				let option = this.sharingOptions.find((opt) => {
					return opt.value == share.chart_id || opt.value === share.chart_id;
				});
				if (!option && this.owners) {
					option = this.owners.find((owner) => {
						return (
							owner.value == share.chart_id || owner.value === share.chart_id
						);
					});
				}
				if (!option) {
					option = {
						text: `Chart ID: ${share.chart_id}`,
						value: share.chart_id,
					};
				}
				return {
					text: option ? option.text : `Chart ID: ${share.chart_id}`,
					value: share.chart_id,
				};
			});
		},

		getSharingName(sharingId) {
			if (!sharingId) {
				return "-";
			}
			if (Array.isArray(sharingId) && sharingId.length === 0) {
				return "-";
			}
			if (typeof sharingId === "object" && sharingId.text) {
				return sharingId.text;
			}
			if (Array.isArray(sharingId) && sharingId.length > 0) {
				if (sharingId.length > 1) {
					return sharingId.map((item) => item.text || item).join(", ");
				}
				return sharingId[0].text || sharingId[0];
			}
			if (this.sharingOptions && Array.isArray(this.sharingOptions)) {
				const sharing = this.sharingOptions.find(
					(sharing) => sharing.value === sharingId
				);
				if (sharing) {
					return sharing.text;
				}
			}
			if (this.owners && Array.isArray(this.owners)) {
				const owner = this.owners.find((owner) => owner.value === sharingId);
				if (owner) {
					return owner.text;
				}
			}

			return sharingId || "-";
		},

		async saveMonthlyData(kpi, ownerIndex, month, year, value) {
			try {
				const ownerSet = kpi.ownerSets[ownerIndex];
				if (!ownerSet || !ownerSet.kpi_item_id) {
					return;
				}
				this.$store.dispatch("setOverlay", true);
				const monthIndex = this.getMonthIndex(month) + 1;
				const periode = `${year}-${monthIndex.toString().padStart(2, "0")}`;
				const numericTarget = this.normalizeNumberInput(value);
				const formattedTarget = this.formatThousandId(numericTarget);
				const formData = new FormData();
				formData.append("kpi_item_id", ownerSet.kpi_item_id);
				formData.append("periode", periode);
				formData.append("target", formattedTarget);
				const response = await this.$axios.post(
					`${process.env.VUE_APP_URL}/api/kpi-value/upsert`,
					formData,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
							"Content-Type": "multipart/form-data",
						},
					}
				);

				console.log(`✅ Auto-save monthly data berhasil:`, response.data);
				await new Promise((resolve) => setTimeout(resolve, 1000));
			} catch (error) {
				console.error("❌ Error auto-saving monthly data:", error);
				this.$emit("show-snackbar", {
					color: "error",
					icon: "mdi-alert",
					mode: "single",
					position: "top",
					text: `Gagal menyimpan data bulanan: ${
						error.response?.data?.message || error.message
					}`,
					timeout: 7500,
					title: "Error",
					visible: true,
				});
				await new Promise((resolve) => setTimeout(resolve, 1000));
			} finally {
				this.$store.dispatch("setOverlay", false);
			}
		},
		async saveData() {
			if (!this.kpiData || this.kpiData.length === 0) {
				this.$emit("show-snackbar", {
					show: true,
					color: "error",
					text: "Tidak ada data KPI untuk disimpan",
				});
				return;
			}
			const valuesToSave = [];
			for (let kpiIndex = 0; kpiIndex < this.kpiData.length; kpiIndex++) {
				const kpi = this.kpiData[kpiIndex];
				if (!kpi.ownerSets || kpi.ownerSets.length === 0) {
					continue;
				}

				if (!kpi.monthlyData || kpi.monthlyData.length === 0) {
					continue;
				}
				for (
					let ownerIndex = 0;
					ownerIndex < kpi.ownerSets.length;
					ownerIndex++
				) {
					const ownerSet = kpi.ownerSets[ownerIndex];
					const monthData = kpi.monthlyData[ownerIndex];
					if (!monthData) {
						continue;
					}
					Object.keys(monthData).forEach((monthKey) => {
						const value = monthData[monthKey];
						if (value !== null && value !== undefined && value !== "") {
							const monthNames = {
								january: "01",
								february: "02",
								march: "03",
								april: "04",
								may: "05",
								june: "06",
								july: "07",
								august: "08",
								september: "09",
								october: "10",
								november: "11",
								december: "12",
							};

							const [monthName, year] = monthKey.split("_");
							const monthNumber = monthNames[monthName.toLowerCase()];
							const periode = `${year}-${monthNumber}`;
							const targetValue = Number.parseFloat(value) || 0;
							const saveItem = {
								kpi_item_id: ownerSet.kpi_item_id,
								periode: periode,
								target: targetValue,
							};

							valuesToSave.push(saveItem);
						}
					});
				}
			}
			if (valuesToSave.length === 0) {
				this.$emit("show-snackbar", {
					show: true,
					color: "warning",
					text: "Tidak ada data yang perlu disimpan",
				});
				return;
			}
			this.$store.dispatch("setOverlay", true);
			try {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await this.$axios.post(
					`${process.env.VUE_APP_URL}/api/kpi-value/upsert-batch`,
					{
						values: valuesToSave,
					}
				);
				this.showSnackbar({
					show: true,
					color: "success",
					title: "Berhasil!",
					text: `Berhasil menyimpan Data KPI`,
					icon: "mdi-check-circle",
					timeout: 3000,
				});
				this.$emit("data-saved");
				this.hasUnsavedChanges = false;
			} catch (error) {
				this.$emit("show-snackbar", {
					show: true,
					color: "error",
					text:
						"Gagal menyimpan data: " +
						(error.response?.data?.message || error.message),
				});
			} finally {
				this.$store.dispatch("setOverlay", false);
			}
		},

		async publishData() {
			let hasValidationError = false;
			this.validationErrors = [];
			for (let kpiIndex = 0; kpiIndex < this.kpiData.length; kpiIndex++) {
				const kpi = this.kpiData[kpiIndex];

				if (!kpi.ownerSets || kpi.ownerSets.length === 0) {
					continue;
				}

				for (
					let ownerIndex = 0;
					ownerIndex < kpi.ownerSets.length;
					ownerIndex++
				) {
					const ownerSet = kpi.ownerSets[ownerIndex];
					const annualTarget = parseFloat(ownerSet.annualTarget) || 0;
					if (ownerSet.manual_adjustment) {
						continue;
					}

					if (!kpi.monthlyData || !kpi.monthlyData[ownerIndex]) {
						continue;
					}

					let monthlySum = 0;
					let monthCount = 0;
					const ownerMonthlyData = kpi.monthlyData[ownerIndex];
					Object.keys(ownerMonthlyData).forEach((monthKey) => {
						const value = ownerMonthlyData[monthKey];

						if (value && value !== "0" && value !== 0) {
							const targetValue = parseFloat(value) || 0;
							monthlySum += targetValue;
							monthCount++;
						}
					});
					monthlySum = parseFloat(monthlySum.toFixed(2));
					const kalkulasi = kpi.kalkulasi ? kpi.kalkulasi.toLowerCase() : "sum";
					let isValid = false;
					let errorMessage = "";
					const ownerName = this.getOwnerName(ownerSet.owner);

					switch (kalkulasi) {
						case "sum": {
							isValid =
								Number(monthlySum.toFixed(2)) ===
								Number(annualTarget.toFixed(2));
							if (!isValid) {
								errorMessage = `KPI "${kpi.kpiName}" - Owner "${ownerName}" - Total target bulanan (${monthlySum}) harus sama dengan Annual Target (${annualTarget})`;
							}
							break;
						}
						case "average": {
							const average = monthCount > 0 ? monthlySum / monthCount : 0;
							isValid = Math.abs(average - annualTarget) < 0.01;
							if (!isValid) {
								errorMessage = `KPI "${
									kpi.kpiName
								}" - Owner "${ownerName}" - Rata-rata target bulanan (${average.toFixed(
									2
								)}) harus sama dengan Annual Target (${annualTarget})`;
							}
							break;
						}
						case "last": {
							let lastValue = 0;
							let lastMonth = "";
							const sortedMonths = this.displayMonths.map(
								(month) => `${month.name}_${month.year}`
							);
							for (let i = sortedMonths.length - 1; i >= 0; i--) {
								const monthKey = sortedMonths[i];
								const value = ownerMonthlyData[monthKey];
								if (value && value !== "0" && value !== 0) {
									lastValue = parseFloat(value) || 0;
									lastMonth = monthKey;
									break;
								}
							}
							isValid = lastValue === annualTarget;
							if (!isValid) {
								errorMessage = `KPI "${kpi.kpiName}" - Owner "${ownerName}" - Nilai bulan terakhir ${lastMonth} (${lastValue}) harus sama dengan Annual Target (${annualTarget})`;
							} else {
								console.log("✅ Last validation passed!");
							}
							break;
						}
						case "max": {
							let maxValue = 0;
							let maxMonth = "";

							const monthKeys = Object.keys(ownerMonthlyData);

							monthKeys.forEach((monthKey) => {
								const value = ownerMonthlyData[monthKey];
								if (value && value !== "0" && value !== 0) {
									const numValue = parseFloat(value) || 0;

									if (numValue > maxValue) {
										maxValue = numValue;
										maxMonth = monthKey;
									}
								}
							});

							isValid = maxValue === annualTarget;
							if (!isValid) {
								errorMessage = `KPI "${kpi.kpiName}" - Owner "${ownerName}" - Nilai tertinggi ${maxMonth} (${maxValue}) harus sama dengan Annual Target (${annualTarget})`;
							} else {
								console.log("✅ Max validation passed!");
							}
							break;
						}
						default: {
							isValid = monthlySum === annualTarget;
							if (!isValid) {
								errorMessage = `KPI "${kpi.kpiName}" - Owner "${ownerName}" - Total target bulanan (${monthlySum}) harus sama dengan Annual Target (${annualTarget})`;
							}
							break;
						}
					}

					if (!isValid) {
						hasValidationError = true;

						this.validationErrors.push({
							kpiIndex: kpiIndex,
							ownerIndex: ownerIndex,
							message: errorMessage,
						});
					}
				}
			}

			if (hasValidationError) {
				this.showSnackbar({
					color: "warning",
					title: "Validasi Gagal",
					text: `Ada ${this.validationErrors.length} baris yang tidak sesuai dengan Annual Target. Periksa baris yang berwarna merah.`,
					icon: "mdi-alert-circle",
					timeout: 8000,
				});
				return;
			}

			try {
				const kpiPeriodeId = this.$route.params.kpiPeriodeId;
				const chartId = this.$route.params.chartId || kpiPeriodeId;

				if (!kpiPeriodeId) {
					throw new Error("KPI Periode ID tidak ditemukan");
				}

				this.isLoading = true;

				const response = await this.$axios.post(
					`${process.env.VUE_APP_URL}/api/kpi-status`,
					{
						chart_id: chartId,
						kpi_periode_id: kpiPeriodeId,
						status: "1",
					},
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
							"Content-Type": "application/json",
						},
					}
				);

				console.log("✅ Publish berhasil:", response.data);

				this.showSnackbar({
					color: "success",
					title: "Berhasil!",
					text: "Data KPI berhasil dipublish!",
					icon: "mdi-check-circle",
					timeout: 3000,
				});

				setTimeout(() => {
					this.$router.push("/home");
				}, 1900);
			} catch (error) {
				console.error("❌ Error mempublish data KPI:", error);

				let errorMessage = "Gagal mempublish data KPI. Silakan coba lagi.";

				if (error.response) {
					if (error.response.status === 401) {
						errorMessage = "Sesi Anda telah berakhir. Silakan login kembali.";
					} else if (error.response.status === 403) {
						errorMessage = "Anda tidak memiliki izin untuk melakukan aksi ini.";
					} else if (error.response.data && error.response.data.message) {
						errorMessage = error.response.data.message;
					}
				} else if (error.request) {
					errorMessage = "Koneksi bermasalah. Periksa koneksi internet Anda.";
				}

				this.showSnackbar({
					color: "error",
					title: "Gagal Publish",
					text: errorMessage,
					icon: "mdi-alert-circle",
					timeout: 8000,
				});
			} finally {
				this.isLoading = false;
			}
		},

		getOwnerName(ownerId) {
			if (!ownerId || !this.owners || !Array.isArray(this.owners)) {
				return ownerId || "-";
			}

			const owner = this.owners.find((owner) => owner.value === ownerId);
			return owner ? owner.text : ownerId;
		},

		setMonthlyData(kpi, ownerIndex, month, year, value) {
			this.hasUnsavedChanges = true;
			if (!kpi.monthlyData || !Array.isArray(kpi.monthlyData)) {
				this.$set(kpi, "monthlyData", []);
			}
			if (!kpi.monthlyData[ownerIndex]) {
				this.$set(kpi.monthlyData, ownerIndex, {});
			}

			const key = `${month}_${year}`;
			this.$set(kpi.monthlyData[ownerIndex], key, value);
			if (!this.isCalculating && ownerIndex > 0) {
				this.autoCalculateParentValues(kpi, month, year, ownerIndex);
			}
		},

		autoCalculateParentValues(kpi, month, year) {
			if (
				kpi &&
				(kpi.statusKpi === "child-parent" || kpi.status_kpi === "child-parent")
			) {
				return;
			}

			const isManualAdjustment =
				kpi.manualAdjustment === true ||
				(kpi.ownerSets &&
					kpi.ownerSets[0] &&
					kpi.ownerSets[0].manual_adjustment === true);

			if (isManualAdjustment) {
				return;
			}

			const isSum = kpi.verticalId === 1;
			const isAverage = kpi.verticalId === 2;
			if (isSum || isAverage) {
				if (kpi.ownerSets && kpi.ownerSets.length > 1 && !this.isCalculating) {
					this.isCalculating = true;

					let total = 0;
					let childCount = 0;
					for (let index = 1; index < kpi.ownerSets.length; index++) {
						const value =
							parseFloat(this.getMonthlyData(kpi, index, month, year)) || 0;

						total += value;
						childCount++;
					}
					let parentValue = 0;
					if (isSum) {
						parentValue = total;
					} else if (isAverage) {
						parentValue = childCount > 0 ? total / childCount : 0;
					}

					if (!kpi.monthlyData[0]) {
						this.$set(kpi.monthlyData, 0, {});
					}
					const key = `${month}_${year}`;
					this.$set(kpi.monthlyData[0], key, parentValue.toString());

					this.isCalculating = false;
				}
			}
		},

		getMonthlyData(kpi, ownerIndex, month, year) {
			if (!kpi.monthlyData || !Array.isArray(kpi.monthlyData)) {
				this.$set(kpi, "monthlyData", []);
			}
			if (!kpi.monthlyData[ownerIndex]) {
				this.$set(kpi.monthlyData, ownerIndex, {});
			}

			const key = `${month}_${year}`;
			const value = kpi.monthlyData[ownerIndex][key] || "";

			return value;
		},

		calculateTotalForOwner(kpi, ownerIndex) {
			if (!kpi.monthlyData || !kpi.monthlyData[ownerIndex]) return 0;

			const monthlyValues = Object.values(kpi.monthlyData[ownerIndex]).map(
				(value) => parseFloat(value) || 0
			);

			if (monthlyValues.length === 0) return 0;
			const kalkulasi = kpi.kalkulasi ? kpi.kalkulasi.toLowerCase() : "sum";

			let tempAmount = 0;
			switch (kalkulasi) {
				case "sum": {
					tempAmount = monthlyValues.reduce((total, value) => total + value, 0);
					break;
				}

				case "average": {
					let sum = 0;
					for (const v of monthlyValues) {
						sum += v;
					}
					tempAmount = Math.round((sum / monthlyValues.length) * 100) / 100;
					break;
				}

				case "last": {
					tempAmount = monthlyValues[monthlyValues.length - 1] || 0;
					break;
				}

				case "max": {
					tempAmount = Math.max(...monthlyValues);
					break;
				}

				default: {
					tempAmount = monthlyValues.reduce((total, value) => total + value, 0);
					break;
				}
			}

			// convert to better looking amount
			let fixedMount = tempAmount.toFixed(2);
			// if fixed amount there is amount behind comma up to 2 decimal and not 0,
			// then we show the comma. if not then we dont show comma
			let amountBehindComma = fixedMount.split(".");
			let finalAmount = 0;
			if (parseFloat(amountBehindComma[1]) > 0) {
				finalAmount = fixedMount;
			} else {
				finalAmount = amountBehindComma[0];
			}

			return finalAmount;
		},

		getKalkulasiName(kalkulasiId) {
			const kalkulasiMap = {
				1: "Sum",
				4: "Last",
				3: "Max",
				2: "Average",
			};
			return kalkulasiMap[kalkulasiId] || "Sum";
		},

		initializeMonthlyData() {
			if (this.kpiData && this.kpiData.length > 0) {
				this.kpiData.forEach((kpi, kpiIndex) => {
					if (!kpi.monthlyData || !Array.isArray(kpi.monthlyData)) {
						this.$set(kpi, "monthlyData", []);
					}
					kpi.ownerSets.forEach((_, index) => {
						if (!kpi.monthlyData[index]) {
							this.$set(kpi.monthlyData, index, {});
						}
						this.displayMonths.forEach((month) => {
							const key = `${month.name}_${month.year}`;

							if (!(key in kpi.monthlyData[index])) {
								this.$set(kpi.monthlyData[index], key, "");
								console.log(
									`🔧 Initialized field: KPI=${kpiIndex}, owner=${index}, key=${key}`
								);
							} else {
								console.log(
									`🔧 Field already exists: KPI=${kpiIndex}, owner=${index}, key=${key}, value=${kpi.monthlyData[index][key]}`
								);
							}
						});
					});
				});
			} else {
				console.log("ℹ️ No KPI data available to initialize monthly data.");
			}
		},
		async fetchKpiPeriodeHeaders() {
			try {
				// if (!this.kpiPeriodeId) {
				// 	await this.fetchKpiPeriodeId();
				// }

				// if (!this.kpiPeriodeId) return;
				const chartId =
					this.$route.params.chartId || this.$route.params.kpiPeriodeId;

				const response = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/kpi-item/period/${chartId}`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
						},
					}
				);

				if (response.data && response.data.headers) {
					this.periodeHeaders = response.data.headers;

					if (response.data.start_month && response.data.end_month) {
						// Add your logic here or remove block
					}
				}
			} catch (error) {
				console.error("❌ Error fetching KPI Periode headers:", error);
			}
		},

		// async fetchKpiPeriodeHeaders() {
		//   try {

		//     if (!this.kpiPeriodeId) {
		//       await this.fetchKpiPeriodeId();
		//     }

		//     if (!this.kpiPeriodeId) {
		//       return;
		//     }
		//     const response = await this.$axios.get(
		//       `${process.env.VUE_APP_URL}/api/kpi-periode/detail/${this.kpiPeriodeId}`,
		//       {
		//         headers: {
		//           Authorization: `Bearer ${this.$store.getters.isTokenUser}`
		//         }
		//       }
		//     );

		//     if (response.data && response.data.headers) {
		//       this.periodeHeaders = response.data.headers;
		//       if (response.data.start_month && response.data.end_month) {
		//       }
		//     }
		//   } catch (error) {
		//    console.error('❌ Error fetching KPI Periode headers:', error);
		//   }
		// },

		getMonthNameFromKey(key) {
			const monthNames = {
				"01": "january",
				"02": "february",
				"03": "march",
				"04": "april",
				"05": "may",
				"06": "june",
				"07": "july",
				"08": "august",
				"09": "september",
				10: "october",
				11: "november",
				12: "december",
			};
			const month = key.split("-")[1];
			return monthNames[month] || "january";
		},

		getDisplayStatus(kpi, ownerIndex) {
			const isSum = kpi.verticalId === 1;
			const isAverage = kpi.verticalId === 2;

			if (
				(isSum || isAverage) &&
				kpi.ownerSets &&
				kpi.ownerSets.length > 1 &&
				kpi.statusKpi !== "child-parent"
			) {
				return ownerIndex === 0 ? "parent" : "";
			}

			return ownerIndex === 0 ? kpi.statusKpi : "";
		},

		getStatusClass(kpi, ownerIndex) {
			if (
				kpi.statusKpi === "child-parent" ||
				kpi.status_kpi === "child-parent"
			) {
				return ownerIndex === 0 ? "status-grand-text" : "status-child-text";
			}
			const isSum = kpi.verticalId === 1;
			const isAverage = kpi.verticalId === 2;

			if (isSum || isAverage) {
				if (kpi.ownerSets && kpi.ownerSets.length > 1) {
					if (ownerIndex === 0) {
						return "status-parent-text";
					} else {
						return "status-child-text";
					}
				}
			}
			if (kpi.statusKpi === "shared") {
				return "status-shared-text";
			}

			if (kpi.statusKpi === "child") {
				return "status-child-text";
			}

			if (kpi.statusKpi === "child-parent") {
				return "status-grand-text";
			}
			return "status-owned-text";
		},

		getRowClass(kpi, kpiIndex, ownerIndex) {
			let classes = [];
			if (this.isRowInvalid(kpiIndex, ownerIndex)) {
				classes.push("row-validation-error");
			}

			return classes.join(" ");
		},

		isRowInvalid(kpiIndex, ownerIndex) {
			return this.validationErrors.some(
				(error) =>
					error.kpiIndex === kpiIndex && error.ownerIndex === ownerIndex
			);
		},

		getOwnerDisplayForShared(kpi, ownerId) {
			if (kpi.statusKpi === "shared") {
				if (this.owners && Array.isArray(this.owners)) {
					const owner = this.owners.find((owner) => {
						return owner.value == ownerId || owner.value === ownerId;
					});
					if (owner) {
						return owner.text;
					}
				}
				if (this.sharingOptions && Array.isArray(this.sharingOptions)) {
					const sharingOwner = this.sharingOptions.find((sharing) => {
						return sharing.value == ownerId || sharing.value === ownerId;
					});

					if (sharingOwner) {
						return sharingOwner.text;
					}
				}
				return `Owner ID: ${ownerId}`;
			}
			return this.getOwnerName(ownerId);
		},

		isFieldDisabled(kpi, ownerIndex) {
			if (kpi.statusKpi === "child") {
				return true;
			}

			if (kpi.statusKpi === "shared") {
				return true;
			}

			const isSum = kpi.verticalId === 1;
			const isAverage = kpi.verticalId === 2;

			if ((isSum || isAverage) && kpi.ownerSets && kpi.ownerSets.length > 1) {
				return ownerIndex === 0;
			}

			return false;
		},

		getMonthIndex(monthName) {
			const months = [
				"january",
				"february",
				"march",
				"april",
				"may",
				"june",
				"july",
				"august",
				"september",
				"october",
				"november",
				"december",
			];
			return months.findIndex((month) => month === monthName.toLowerCase());
		},

		getVerticalText(verticalId) {
			const verticalMap = {
				1: "Sum",
				2: "Average",
			};
			return verticalMap[verticalId] || "";
		},

		IsChildParent(kpi) {
			return !!(kpi && kpi.status_kpi === "child-parent");
		},
	},
};
</script>

<style scoped>
.kpi-info-table,
.monthly-data-table {
	overflow-x: auto;
	white-space: nowrap;
}

.kpi-info-table >>> thead th,
.monthly-data-table >>> thead th,
.owner-sets-table >>> thead th {
	background-color: #003871;
	color: white;
	font-weight: 600;
	padding: 12px 8px;
	text-transform: uppercase;
	font-size: 0.875rem;
	white-space: nowrap;
	position: sticky;
	top: 0;
	z-index: 2;
	text-align: center;
	height: 48px;
}

.kpi-info-table >>> tbody td,
.monthly-data-table >>> tbody td,
.owner-sets-table >>> tbody td {
	padding: 4px !important;
	vertical-align: middle !important;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	text-align: center;
	height: 48px !important;
	display: table-cell !important;
	color: rgba(0, 0, 0, 0.87);
}

.kpi-name-cell {
	vertical-align: middle !important;
	text-align: center !important;
	font-weight: 500 !important;
	background-color: rgba(0, 56, 113, 0.05) !important;
	min-width: 300px !important;
	max-width: 400px !important;
	white-space: normal !important;
	word-wrap: break-word !important;
	overflow-wrap: break-word !important;
}

.month-input {
	width: 150px;
	margin: 0 auto !important;
}

.month-input >>> input {
	text-align: right !important;
}

.month-input >>> .v-input__control input {
	text-align: right !important;
}

.status-shared-text {
	color: #ffffff !important;
	background-color: #ff9800 !important;
	font-weight: 600;
	padding: 8px 12px;
	border-radius: 4px;
}

.status-child-text {
	color: #ffffff !important;
	background-color: #2196f3 !important;
	font-weight: 600;
	padding: 8px 12px;
	border-radius: 4px;
}
.status-grand-text {
	color: #ffffff !important;
	background-color: #7c21f3 !important;
	font-weight: 600;
	padding: 8px 12px;
	border-radius: 4px;
}

.row-validation-error {
	background-color: #ffebee !important;
	border-left: 4px solid #f44336 !important;
}

.row-validation-error td {
	color: #c62828 !important;
	font-weight: 500 !important;
}

.status-parent-text {
	color: #ffffff !important;
	background-color: #4caf50 !important;
	font-weight: 600;
	padding: 8px 12px;
	border-radius: 4px;
}

.status-owned-text {
	color: #ffffff !important;
	background-color: #4caf50 !important;
	font-weight: 600;
	padding: 8px 12px;
	border-radius: 4px;
}

.monthly-data-table >>> thead th:nth-child(3) {
	white-space: nowrap !important;
}

.monthly-data-table >>> tbody td:nth-child(3) {
	white-space: normal !important;
	overflow-wrap: anywhere;
	word-break: break-word;
	line-height: 1.4;
	min-width: 180px;
	max-width: 260px;
	text-align: center;
	padding-right: 12px;
}

@media (max-width: 960px) {
	.kpi-info-table,
	.monthly-data-table {
		overflow-x: auto;
	}

	.month-input {
		width: 150px;
	}

	.month-input >>> input {
		text-align: right !important;
	}

	.month-input >>> .v-input__control input {
		text-align: right !important;
	}
}
</style>